import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Layers3,
  Play,
  Sparkles,
  Target,
} from 'lucide-react';
import { getChapters } from '../../services/mockTestService';
import { getSubjects } from '../../services/subjectCombinationService';
import { getMockTestUsage } from '../../services/generateQuestionService';
import { useProtectedRoute } from '../../hooks/useProtectedRoute';

interface Book {
  name: string;
  chapters: string[];
}

interface ChapterData {
  subject: string;
  board: string;
  classLevel: string;
  country: string;
  books: Book[];
}

const MockTestPage: React.FC = () => {
  useProtectedRoute(); // Protect route from direct URL access when credits exhausted

  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<string[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [chaptersData, setChaptersData] = useState<ChapterData | null>(null);
  const [selectedChapters, setSelectedChapters] = useState<string[]>([]);
  const [expandedBooks, setExpandedBooks] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [subjectLoading, setSubjectLoading] = useState<boolean>(true);
  const [feedback, setFeedback] = useState<string>('');
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [limitModalMessage, setLimitModalMessage] = useState('');
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);

  useEffect(() => {
    const fetchSubjects = async () => {
      setSubjectLoading(true);
      try {
        const response = await getSubjects();
        setSubjects(response.subjects || []);
        if (response.subjects.length > 0) {
          setSelectedSubject(response.subjects[0]);
        }
      } catch (err) {
        setError('Failed to load subjects.');
      } finally {
        setSubjectLoading(false);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    const fetchChapters = async () => {
      if (!selectedSubject) return;
      setLoading(true);
      try {
        const response = await getChapters(selectedSubject);
        setChaptersData(response);
        setError(null);
      } catch (err) {
        setError('Failed to load chapters for the selected subject.');
        setChaptersData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchChapters();
  }, [selectedSubject]);

  const handleSubjectChange = (subject: string) => {
    setSelectedSubject(subject);
    setSelectedChapters([]);
    setExpandedBooks([]);
  };

  const toggleBookExpansion = (bookName: string) => (
    _: React.SyntheticEvent,
    expanded: boolean,
  ) => {
    setExpandedBooks((prev) =>
      expanded ? [...prev, bookName] : prev.filter((name) => name !== bookName),
    );
  };

  const handleBookSelect = (book: Book) => {
    const bookChapters = book.chapters;
    const allSelected = bookChapters.every((chapter) => selectedChapters.includes(chapter));
    if (allSelected) {
      setSelectedChapters((prev) => prev.filter((chapter) => !bookChapters.includes(chapter)));
    } else {
      setSelectedChapters((prev) => Array.from(new Set([...prev, ...bookChapters])));
    }
  };

  const handleChapterSelect = (chapter: string) => {
    setSelectedChapters((prev) =>
      prev.includes(chapter) ? prev.filter((ch) => ch !== chapter) : [...prev, chapter],
    );
  };

  const startQuiz = async () => {
    if (selectedChapters.length === 0) {
      return;
    }

    setIsCheckingLimit(true);
    try {
      const usage = await getMockTestUsage();

      if (usage.isLimitReached) {
        const planLabel = usage.planName || 'Free Plan';
        setLimitModalMessage(
          `You have reached your daily mock generation limit for ${planLabel} (${usage.todayUsageCount}/${usage.dailyLimit}). Please try again after the daily reset.`,
        );
        setIsLimitModalOpen(true);
        return;
      }

      navigate('/quiz', { state: { selectedChapters, selectedSubject, feedback } });
    } catch (err) {
      setError('Failed to validate daily mock test limit. Please try again.');
    } finally {
      setIsCheckingLimit(false);
    }
  };

  return (
    <Stack spacing={4}>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'rgba(90, 75, 252, 0.12)',
                      color: 'primary.dark',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Layers3 size={22} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      Build an adaptive mock
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a subject and the chapters you want the AI examiner to focus on.
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mb={2}
                  >
                    <Sparkles size={18} color="#5a4bfc" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      1. Choose the subject to focus on
                    </Typography>
                  </Stack>

                  {subjectLoading ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Fetching your subjects…
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {subjects.map((subject) => (
                        <Chip
                          key={subject}
                          label={subject}
                          onClick={() => handleSubjectChange(subject)}
                          color={selectedSubject === subject ? 'primary' : 'default'}
                          variant={selectedSubject === subject ? 'filled' : 'outlined'}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 600,
                            px: 1.5,
                            py: 1,
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mb={2}
                  >
                    <Target size={18} color="#00b894" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      2. Select books & chapters to test
                    </Typography>
                  </Stack>

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  {loading ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Preparing AI structured chapters…
                      </Typography>
                    </Stack>
                  ) : chaptersData && chaptersData.books && chaptersData.books.length > 0 ? (
                    <Stack spacing={1.5}>
                      {chaptersData.books.map((book) => {
                        const isExpanded = expandedBooks.includes(book.name);
                        const allChaptersSelected = book.chapters.every((chapter) =>
                          selectedChapters.includes(chapter),
                        );
                        return (
                          <Accordion
                            key={book.name}
                            expanded={isExpanded}
                            onChange={toggleBookExpansion(book.name)}
                            disableGutters
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(90, 75, 252, 0.12)',
                              boxShadow: 'none',
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ChevronDown size={18} />}
                              sx={{ px: 2.5 }}
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{ width: '100%' }}
                                justifyContent="space-between"
                              >
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Checkbox
                                    checked={allChaptersSelected}
                                    onChange={(event) => {
                                      event.stopPropagation();
                                      handleBookSelect(book);
                                    }}
                                    onClick={(event) => event.stopPropagation()}
                                  />
                                  <Stack>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {book.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {book.chapters.length} chapters available
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0, px: 2.5, pb: 2.5 }}>
                              <Stack spacing={1}>
                                {book.chapters.map((chapter) => (
                                  <Box
                                    key={chapter}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      borderRadius: 2,
                                      border: '1px solid rgba(17, 24, 39, 0.08)',
                                      px: 2,
                                      py: 1.25,
                                      transition: 'background-color 0.2s ease',
                                      backgroundColor: selectedChapters.includes(chapter)
                                        ? 'rgba(90, 75, 252, 0.08)'
                                        : 'transparent',
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={selectedChapters.includes(chapter)}
                                          onChange={() => handleChapterSelect(chapter)}
                                        />
                                      }
                                      label={
                                        <Typography variant="body2" fontWeight={500}>
                                          {chapter}
                                        </Typography>
                                      }
                                      sx={{ flexGrow: 1, m: 0 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      Adaptive difficulty
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      No AI-generated books are available for this subject yet. Pick another subject or
                      check back soon.
                    </Alert>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={700}>
                    Mock configuration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    A tailored mock will be generated based on your selections with AI-marked feedback and
                    revision prompts.
                  </Typography>
                  <Divider />
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Selected subject
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {selectedSubject || '—'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Chapters chosen
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {selectedChapters.length}
                      </Typography>
                    </Stack>
                  </Stack>
                  <TextField
                    label="Learning goal for this mock (optional)"
                    placeholder="Share topics to emphasise, exam conditions, timing preferences…"
                    value={feedback}
                    onChange={(event) => setFeedback(event.target.value)}
                    multiline
                    minRows={4}
                    fullWidth
                  />

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Play size={18} />}
                    onClick={startQuiz}
                    disabled={selectedChapters.length === 0 || isCheckingLimit}
                  >
                    {isCheckingLimit ? 'Checking daily limit...' : 'Launch adaptive mock'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={700}>
                    Before you start
                  </Typography>
                  <Stack component="ul" spacing={1.5} sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      These chapters are AI-generated and may not fully align with your official curriculum.
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Complete the mock in one sitting to capture accurate performance data.
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Review the AI debrief afterwards for targeted revision prompts.
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Exam mode saves progress locally; best experienced on desktop or tablet.
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={isLimitModalOpen} onClose={() => setIsLimitModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Daily mock limit reached</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {limitModalMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLimitModalOpen(false)} variant="contained">
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MockTestPage;
